/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-empty-object-type */
import {
  AssetsReadyForValidationResponseData,
  AssignUserToEpisodesRequestData,
  ValidateAssetRequestData,
} from "@/api/validation-types";
import { env } from "@/env";
import { Credentials } from "./auth-types";
import {
  EpisodeBase,
  EpisodeDetails,
  EpisodeSummarized,
  GridRequestQueryParams,
  PostEpisodeData
} from "./episodes-types";
import {
  ChangeIsChildPresentedData,
  GenerationStatusResponse,
  ImageBase,
  ImageDetails,
  ImageNeedingManualEdit,
  ImageParams,
  ImageUploadResponseData,
  ManualEditState,
} from "./images-types";
import { MaskDetails, MaskWithImage } from "./masks-types";
import { CreateMasterCombination, MasterCombination, MasterCombinationWithRelations } from "./master-combinations-types";
import {
  CreatePrompt,
  CreatePromptRevision,
  Prompt,
  PromptRevision,
  UpdatePrompt,
} from "./prompts-types";
import { RejectionReasonsResponse } from "./statistics-types";
import {
  RejectStory,
  RejectStoryResponse,
  StoryGenerationMetadata,
} from "./stories-types";
import { CreateStoryConfig, CreateStoryConfigRevision, StoryConfig, StoryConfigRevision } from "./story-configs-types";
import {
  AssetsReadyForSubmissionResponseData,
  SubmitAssetsRequestData,
} from "./submission-types";
import { User } from "./users-types";
import {
  CursorPaginationParams,
  PaginatedData,
  PaginationParams,
} from "./utils";

type QueryDict = Record<
  string,
  boolean | string | number | (boolean | number | string)[] | undefined
>;

type ResponseOptions = {
  asBlob?: boolean;
};

function buildUrl(path: string, queryDict?: QueryDict) {
  const params = new URLSearchParams();
  if (queryDict) {
    Object.entries(queryDict).forEach(([key, value]) => {
      if (value === undefined) return;
      if (Array.isArray(value)) {
        value.forEach((v) => params.append(key, String(v)));
      } else {
        params.append(key, String(value));
      }
    });
  }
  const query = params.toString();
  return env.API_URL + "/api/" + path + (query ? "?" + query : "");
}

const headers = {
  "Accept": "application/json", // prettier-ignore
  "Content-Type": "application/json",
};

async function makeRequest<TRequestData, TResponseData>(
  path: string,
  queryDict?: QueryDict,
  data?: TRequestData,
  init?: RequestInit,
  responseOptions?: ResponseOptions,
): Promise<TResponseData> {
  const isFormData = data instanceof FormData;
  const requestHeaders = isFormData
    ? { ...init?.headers }
    : { ...headers, ...init?.headers };

  const response = await fetch(buildUrl(path, queryDict), {
    body: isFormData ? data : JSON.stringify(data),
    credentials: "include", // so that cookies are saved when API is on a different port than the frontend.
    ...init,
    headers: requestHeaders,
  });
  const { status, statusText } = response;
  if (status >= 200 && status <= 299) {
    if (responseOptions?.asBlob) {
      const blob = await response.blob();
      return blob as TResponseData;
    }
    const data = await response.json();
    return data;
  }
  if (status >= 400 && status <= 499) {
    if (status === 401) {
      window.dispatchEvent(new CustomEvent("Unauthenticated"));
    }
    throw { status, statusText, data: await response.json() };
  }
  if (status >= 500) {
    throw { status, statusText, data: await response.text() };
  }
  throw new Error("Not implemented");
}

function get<TResponseData>(
  path: string,
  queryDict?: QueryDict,
  responseOptions?: ResponseOptions,
) {
  return makeRequest<undefined, TResponseData>(
    path,
    queryDict,
    undefined,
    {
      method: "GET",
    },
    responseOptions,
  );
}

function post<TRequestData, TResponseData>(
  path: string,
  data?: TRequestData,
  queryDict?: QueryDict,
) {
  return makeRequest<TRequestData, TResponseData>(path, queryDict, data, {
    method: "POST",
  });
}

function patch<TRequestData, TResponseData>(
  path: string,
  data?: TRequestData,
  queryDict?: QueryDict,
) {
  return makeRequest<TRequestData, TResponseData>(path, queryDict, data, {
    method: "PATCH",
  });
}

// delete is a reserved keyword, so we use "remove" instead
function remove<TRequestData, TResponseData>(
  path: string,
  data?: TRequestData,
  queryDict?: QueryDict,
) {
  return makeRequest<TRequestData, TResponseData>(path, queryDict, data, {
    method: "DELETE",
  });
}

export const api = {
  auth: {
    post: (data: Credentials) => post<Credentials, User>("auth", data),
    status: {
      get: () => get<{ user: User }>("auth/status"),
    },
    logout: {
      post: () => post("auth/logout"),
    },
  },
  episodes: {
    get: ({
      page = 1,
      pageSize = 50,
      ...queryParams
    }: GridRequestQueryParams) =>
      get<PaginatedData<EpisodeSummarized>>("episodes", {
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...queryParams,
      }),
    addTag: {
      patch: (data: { episodeIds: string[]; tag: string }) =>
        patch<{ episodeIds: string[]; tag: string }, {}>(
          "episodes/addTag",
          data,
        ),
    },
    removeTag: {
      patch: (data: { episodeIds: string[]; tag: string }) =>
        patch<{ episodeIds: string[]; tag: string }, {}>(
          "episodes/removeTag",
          data,
        ),
    },
    episodeId: {
      get: ({ episodeId }: { episodeId: string }) =>
        get<EpisodeDetails>(`episodes/${episodeId}`),
      images: {
        get: ({ episodeId }: { episodeId: string }) =>
          get<{ images: ImageBase[] }>(`episodes/${episodeId}/images`),
      },
      imagesForManualEdit: {
        get: ({ episodeId }: { episodeId: string }) =>
          get<Blob>(`episodes/${episodeId}/images-for-manual-edit`, undefined, {
            asBlob: true,
          }),
      },
      texts: {
        get: ({ episodeId }: { episodeId: string }) =>
          get<{ pages: { pageNumber: number; text: string }[] }>(
            `episodes/${episodeId}/texts`,
          ),
      },
      masks: {
        get: ({ episodeId }: { episodeId: string }) =>
          get<{ masks: MaskWithImage[] }>(`episodes/${episodeId}/masks`),
      },
    },
  },
  images: {
    imageId: {
      get: ({ imageId }: ImageParams) => get<ImageDetails>(`images/${imageId}`),
      changeIsChildPresented: {
        post: ({ imageId }: ImageParams, data: ChangeIsChildPresentedData) =>
          post<ChangeIsChildPresentedData, {}>(
            `images/${imageId}/changeIsChildPresented`,
            data,
          ),
      },
      manualEditState: {
        patch: (
          { imageId }: ImageParams,
          data: { manualEditState: ManualEditState },
        ) => patch<{}, {}>(`images/${imageId}/manualEditState`, data),
      },
      imageUpload: {
        post: ({
          imageId,
          formData,
        }: {
          imageId: string;
          formData: FormData;
        }) =>
          post<FormData, ImageUploadResponseData>(
            `images/${imageId}/image-upload`,
            formData,
          ),
      },
      maskUpload: {
        post: ({
          imageId,
          formData,
        }: {
          imageId: string;
          formData: FormData;
        }) =>
          post<FormData, MaskWithImage>(
            `images/${imageId}/mask-upload`,
            formData,
          ),
      },
    },
    needingManualEdit: {
      get: (queryParams: GridRequestQueryParams) =>
        get<PaginatedData<ImageNeedingManualEdit>>("images/needingManualEdit", {
          ...queryParams,
          ...(queryParams.page !== undefined
            ? { page: queryParams.page.toString() }
            : {}),
          ...(queryParams.pageSize !== undefined
            ? { pageSize: queryParams.pageSize.toString() }
            : {}),
        } as GridRequestQueryParams),
    },
    checkGenerationStatus: {
      generationId: {
        get: ({ generationId }: { generationId: string }) =>
          get<GenerationStatusResponse>(`images/checkGenerationStatus/${generationId}`),
      },
    }
  },
  masks: {
    maskId: {
      get: ({ maskId }: { maskId: string }) =>
        get<MaskDetails>(`masks/${maskId}`),
    },
  },
  masterCombinations: {
    get: (params?: PaginationParams) =>
      get<PaginatedData<MasterCombinationWithRelations>>("master-combinations", params),
    post: (data: CreateMasterCombination) =>
      post<CreateMasterCombination, MasterCombination>("master-combinations", data),
  },
  submission: {
    assetsReadyForSubmission: {
      get: (params: QueryDict) =>
        get<AssetsReadyForSubmissionResponseData>(
          "submission/assetsReadyForSubmission",
          params,
        ),
    },
    submitAssets: {
      post: (data: SubmitAssetsRequestData) =>
        post<SubmitAssetsRequestData, {}>("submission/submitAssets", data),
    },
  },
  validation: {
    assetsReadyForValidation: {
      get: () =>
        get<AssetsReadyForValidationResponseData>(
          "validation/assetsReadyForValidation",
        ),
    },
    assignUserToEpisodes: {
      post: (data: AssignUserToEpisodesRequestData) =>
        post<AssignUserToEpisodesRequestData, {}>(
          "validation/assignUserToEpisodes",
          data,
        ),
    },
    validateImage: {
      post: (data: ValidateAssetRequestData) =>
        post<ValidateAssetRequestData, {}>("validation/validateImage", data),
    },
    validateMask: {
      post: (data: ValidateAssetRequestData) =>
        post<ValidateAssetRequestData, {}>("validation/validateMask", data),
    },
  },
  statistics: {
    rejectionReasons: {
      get: (params?: CursorPaginationParams) =>
        get<RejectionReasonsResponse>("statistics/rejectionReasons", params),
    },
  },
  stories: {
    post: (data: PostEpisodeData) =>
      post<PostEpisodeData, { episodes: EpisodeBase[] }>("stories", data),
    episodeId: {
      metadata: {
        get: (episodeId: string) =>
          get<StoryGenerationMetadata>(`stories/${episodeId}/metadata`),
      },
      reject: {
        post: (episodeId: string, data: RejectStory) =>
          post<RejectStory, RejectStoryResponse>(
            `stories/${episodeId}/reject`,
            data,
          ),
      },
    },
  },
  storyConfigs: {
    get: () => get<{ items: StoryConfig[] }>("storyConfigs"),
    post: (data: CreateStoryConfig) => post<CreateStoryConfig, StoryConfig>("storyConfigs", data),
    configId: {
      get: (configId: string) => get<StoryConfig>(`storyConfigs/${configId}`),
      revisions: {
        post: (configId: string, data: CreateStoryConfigRevision) =>
          post<CreateStoryConfigRevision, StoryConfigRevision>(`storyConfigs/${configId}/revisions`, data),
      },
    },
  },
  prompts: {
    get: (params?: PaginationParams) =>
      get<PaginatedData<Prompt>>("prompts", params),
    post: (data: CreatePrompt) => post<CreatePrompt, Prompt>("prompts", data),
    promptId: {
      get: (promptId: string) => get<Prompt>(`prompts/${promptId}`),
      patch: (promptId: string, data: UpdatePrompt) =>
        patch<UpdatePrompt, Prompt>(`prompts/${promptId}`, data),
      delete: (promptId: string) => remove(`prompts/${promptId}`),
      revisions: {
        post: (promptId: string, data: CreatePromptRevision) =>
          post<CreatePromptRevision, PromptRevision>(
            `prompts/${promptId}/revisions`,
            data,
          ),
      },
    },
  },
  tags: {
    get: () => get<string[]>("tags"),
  },
};

// api.auth.post(data) // POST api/auth
// api.auth.post(data, { x: 1 }) // POST api/auth?x=1
// api.auth.status.get() // GET api/auth/status
// api.users.get() // GET /api/users
// api.users.get({ x: 1}) // GET /api/users?x=1
// api.users.userId.posts.postId.get({ postId, userId }) // GET api/users/123/posts/456
// api.users.userId.posts.postId.get({ postId, userId }, { x: 1 }) // GET api/users/123/posts/456?x=1
// api.users.userId.posts.postId.post({ postId, userId }, data) // POST api/users/123/posts/456
// api.users.userId.posts.postId.post({ postId, userId }, data, { x: 1 }) // POST api/users/123/posts/456?x=1

export type ApiError = {
  status: number;
  statusText: string;
  data:
  | string
  | {
    error: {
      code: string;
      message: string;
      data: Record<string, any>;
    };
  };
};
